import { useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

type ChecklistSignatureDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  itemTitle: string
  isPending?: boolean
  onSubmit: (payload: { signatureFile: File; customerName: string }) => void
}

const CANVAS_WIDTH = 560
const CANVAS_HEIGHT = 180

export function ChecklistSignatureDialog({
  open,
  onOpenChange,
  itemTitle,
  isPending = false,
  onSubmit,
}: ChecklistSignatureDialogProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const drawingRef = useRef(false)
  const hasInkRef = useRef(false)
  const [customerName, setCustomerName] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)

  const resetCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) {
      return
    }

    const context = canvas.getContext('2d')
    if (!context) {
      return
    }

    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.strokeStyle = '#111827'
    context.lineWidth = 2
    context.lineCap = 'round'
    context.lineJoin = 'round'
    hasInkRef.current = false
  }

  useEffect(() => {
    if (!open) {
      setCustomerName('')
      setValidationError(null)
      return
    }

    resetCanvas()
  }, [open])

  const getCanvasPoint = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) {
      return null
    }

    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    }
  }

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    const point = getCanvasPoint(event)
    const context = canvas?.getContext('2d')
    if (!canvas || !point || !context) {
      return
    }

    drawingRef.current = true
    canvas.setPointerCapture(event.pointerId)
    context.beginPath()
    context.moveTo(point.x, point.y)
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) {
      return
    }

    const canvas = canvasRef.current
    const point = getCanvasPoint(event)
    const context = canvas?.getContext('2d')
    if (!canvas || !point || !context) {
      return
    }

    context.lineTo(point.x, point.y)
    context.stroke()
    hasInkRef.current = true
    setValidationError(null)
  }

  const stopDrawing = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) {
      return
    }

    drawingRef.current = false
    canvasRef.current?.releasePointerCapture(event.pointerId)
  }

  const handleSubmit = async () => {
    const canvas = canvasRef.current
    if (!canvas || !hasInkRef.current) {
      setValidationError('Draw the customer signature before completing this step.')
      return
    }

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/png')
    })

    if (!blob) {
      setValidationError('Could not capture the signature. Try again.')
      return
    }

    onSubmit({
      signatureFile: new File([blob], 'signature.png', { type: 'image/png' }),
      customerName: customerName.trim(),
    })
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !isPending && onOpenChange(nextOpen)}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Customer signature required</DialogTitle>
          <DialogDescription>
            Capture the customer signature for &quot;{itemTitle}&quot; before marking this checklist step complete.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="checklist-signature-customer-name">Customer name</Label>
            <Input
              id="checklist-signature-customer-name"
              value={customerName}
              onChange={(event) => setCustomerName(event.target.value)}
              placeholder="Optional"
              disabled={isPending}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label>Signature</Label>
              <Button type="button" variant="ghost" size="sm" onClick={resetCanvas} disabled={isPending}>
                Clear
              </Button>
            </div>
            <div className="overflow-hidden rounded-xl border border-border/70 bg-white">
              <canvas
                ref={canvasRef}
                width={CANVAS_WIDTH}
                height={CANVAS_HEIGHT}
                className="block h-44 w-full touch-none cursor-crosshair"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={stopDrawing}
                onPointerLeave={stopDrawing}
                onPointerCancel={stopDrawing}
              />
            </div>
            <p className="text-xs text-muted-foreground">Sign with mouse, trackpad, or touch.</p>
            {validationError ? <p className="text-sm text-destructive">{validationError}</p> : null}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isPending}>
            {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save signature and complete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
